import { useBoolean } from '@chakra-ui/hooks'
import { useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import {
  createContext,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import { FaHistory } from 'react-icons/fa'
import { useImmer } from 'use-immer'

import api from '@config/api'

import { ScheduleItem } from '@component/Modules/ConfigPurchase/types'

import { parseParametrizatioCurveStoreData } from '@helper/grid/parametrization-curve'
import { parseParametrizationPurchaseCronogramaExport } from '@helper/grid/parametrization-purchase'
import {
  parseConfigAlcadas,
  parseConfigFerias,
  parseConfigUser
} from '@helper/parametrization/parseConfigPurchase'
import {
  getPurchaseConfigTokenClaims,
  isPurchaseConfigAdmin
} from '@helper/parametrization/purchaseConfigAuth'
import paramsHelper from '@helper/params'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import parametrization from '@service/parametrization'
import parametrizationCurves from '@service/parametrization-curves'
import ParametroCompra from '@service/ParametroCompra'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  AtualizarCronogramaComprasCommand,
  IConfigPurchaseParamProvider
} from '@base/@types/providers/Parametrization/ConfigParamPurchase'
import type {
  IParams,
  IParamsEdit,
  Pagination,
  ITooglesParams,
  IUpdateParams,
  IUpdateParamsEdit,
  IUpdateParamsDate,
  IDefaultDate
} from '@base/@types/providers/Parametrization/ConfigReabProvider'
import { IFilter } from '@base/@types/providers/Shared/FiltersProvider'
import parametrizationCurveStoretHeader from '@base/variables/headers/parametrization/curve-store'

// Representação do cronograma independente da ordem e dos ids temporários
const serializeCronograma = (items: ScheduleItem[]): string =>
  items
    .map(
      item =>
        `${item.lojaId}|${item.fornecedorId}|${item.categoriaCompactaId}|${item.diaDaSemana}`
    )
    .sort()
    .join(';')

const ParametrizationConfigPurchaseProvider: FC = ({ children }) => {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [fastQuery, _setFastQuery] = useState<string>('')
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('PRM Novas Lojas - Relatório')
  const [_selected, _setSelected] = useState<any>()
  const [fetching, _setFetching] = useState(false)
  const [_error, setError] = useState<string | undefined>()
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [pageCurrent, setPageCurrent] = useState(1)
  const [total, setTotal] = useState(10)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [companieId, setCompanieId] = useState('')

  const [activeTimeline, setActiveTimeLine] = useState(0)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [selecteds, setSelecteds] = useImmer([] as any)

  const [data, updateData] = useImmer<any[]>([])
  const [selected, setSelectedOne] = useState(0)

  const handleSelectAll = useCallback(() => {
    if (selecteds.length < data.length) {
      const res = []
      for (const item of data) {
        res.push(item.id)
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }, [selecteds, data, setSelecteds])

  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [filterEdit, updateCenters] = useImmer({
    centers: [],
    products: []
  })

  const [tooglesTable, updateToogleParams] = useImmer({
    pendentes: true,
    aprovados: true,
    recusados: true
  })

  const handleToogleTable = useCallback(
    ({ name, value }: ITooglesParams) => {
      updateToogleParams(draft => {
        draft[name] = value
      })
    },
    [updateToogleParams]
  )

  // Alcadas
  const [dataAlcadas, updateDataAlcadas] = useImmer<any[]>([])
  const [loadingAlcadas, setLoadingAlcadas] = useState(false)
  const dateNow = new Date()
  const dateEnd = dateNow.setDate(dateNow.getDate() - 30)
  const [updateAlcadaOb, setupdateAlcadaOb] = useImmer<any>(null)
  const [selectedAlcada, setSelectedAlcada] = useState(0)

  // USERS
  const [dataUser, updateDataUsers] = useImmer<any[]>([])
  const [loadingUser, setLoadingUsers] = useState(false)
  const [updateUserOb, setupdateUserOb] = useImmer<any[]>([])
  const [selectedUser, setSelectedUser] = useState(0)

  // FERIAS
  const [dataFerias, updateDataFerias] = useImmer<any[]>([])
  const [loadingFerias, setLoadingFerias] = useState(false)
  const [updateFeriasOb, setupdateFeriasOb] = useImmer<any>(null)
  const [selectedFerias, setSelectedFerias] = useState(0)

  // Cronograma
  const [cronogramaCompra, setCronogramaCompra] = useState<ScheduleItem[]>([])
  const [loadingCronograma, setLoadingCronograma] = useState(false)
  // true quando a busca falhou (exceto 400 = sem cronograma): calendário vazio não reflete o banco
  const [cronogramaLoadFailed, setCronogramaLoadFailed] = useState(false)
  // Retrato do cronograma como está no banco (carga/salvar): base do aviso de alterações não salvas
  const [cronogramaBaseline, setCronogramaBaseline] = useState('')
  // Empresa/comprador a que pertence o calendário na tela; o Salvar grava exatamente nele
  const cronogramaOwner = useRef<{
    empresaId: number
    usuarioId: number
  } | null>(null)

  // Compradores
  const [compradores, setCompradores] = useState<
    Array<{ id: number; nome: string; email?: string }>
  >([])
  const [selectedComprador, setSelectedComprador] = useState<number | null>(
    null
  )
  // Espelho síncrono da seleção, para callbacks não lerem valor antigo
  const selectedCompradorRef = useRef<number | null>(null)
  const [loadingCompradores, setLoadingCompradores] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  // Cache: guarda a quem pertencem os dados que estão na tela (empresa e, no cronograma, empresa:comprador).
  // Cada lista é um estado único, então só dá para pular a busca se ela já for da mesma empresa.
  // Ref (síncrono) para que limpar o cache valha na mesma chamada, sem esperar re-render.
  type CacheType = 'alcadas' | 'ferias' | 'users' | 'cronograma'
  const loadedDataCache = useRef<Partial<Record<CacheType, string>>>({})
  // Sequência por tipo: descarta respostas antigas quando a empresa/comprador troca rápido
  const requestSeq = useRef<Record<CacheType, number>>({
    alcadas: 0,
    ferias: 0,
    users: 0,
    cronograma: 0
  })
  // Nova busca: invalida o cache do tipo (a lista na tela vai mudar) e devolve o número da requisição
  // (só usam refs, então as funções são estáveis e não recriam os callbacks que dependem delas)
  const nextRequest = useCallback((dataType: CacheType) => {
    delete loadedDataCache.current[dataType]
    return ++requestSeq.current[dataType]
  }, [])
  const isLatestRequest = useCallback(
    (dataType: CacheType, seq: number) => requestSeq.current[dataType] === seq,
    []
  )

  // Funções helper para cache
  const isDataLoaded = useCallback(
    (cacheKey: string | number, dataType: CacheType) =>
      loadedDataCache.current[dataType] === String(cacheKey),
    []
  )

  const markDataAsLoaded = useCallback(
    (cacheKey: string | number, dataType: CacheType) => {
      loadedDataCache.current[dataType] = String(cacheKey)
    },
    []
  )

  const clearDataCache = useCallback(() => {
    loadedDataCache.current = {}
  }, [])

  const [paramsDates, updateDatesParams] = useImmer({
    dateInit: dayjs(dateEnd).format('YYYY-MM-DD'),
    dateEnd: dayjs(new Date()).format('YYYY-MM-DD')
  })

  const parseCommonFilters = (values: any[]): IFilter[] => {
    return values.map((filter: any) => ({
      value: filter.id || filter.codigo,
      label: `${filter.id || filter.codigo} - ${filter.descricao}`
    }))
  }

  const handleParamsDate = useCallback(
    ({ name, value }: IUpdateParamsDate) => {
      updateDatesParams(draft => {
        draft[name] = value
      })
    },
    [updateDatesParams]
  )

  const [paramsEdit, updateEditParams] = useImmer<IParamsEdit>({
    lojaIds: [],
    produtoIds: [],
    dataInicio: new Date().toISOString().split('T')[0],
    dataFim: new Date().toISOString().split('T')[0]
  })

  const handleUpdateEditParams = useCallback(
    async ({ name, value }: IUpdateParamsEdit) => {
      updateEditParams(draft => {
        draft[name] = value
      })
    },
    [updateEditParams]
  )
  const getCenters = useCallback(async () => {
    setLoadingEdit(true)
    try {
      const { data: centers } = await parametrization.getStore()
      const { data: products } = await parametrization.getProducts()

      updateCenters(draft => {
        return Object.assign(draft, {
          centers: parseCommonFilters(centers.data),
          products: parseCommonFilters(products.data)
        })
      })
    } catch (error: any) {
      const { data } = error.response ?? {}

      setError(
        data?.message ||
          'Ocorreu um problema interno. Tente novamente mais tarde'
      )
    }
    setLoadingEdit(false)
  }, [updateCenters])

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const initialDate = dayjs(dateNow).subtract(1, 'year').format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).add(1, 'year').format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    mundoIds: [],
    categoriasCompactasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    empresaIds: [],
    statusIds: [],
    curvaAbc: [],
    curvaIgualZero: false,
    compradores: [],
    periodo: new Date().toISOString(),
    produtoSemFornecedor: false,
    initialDate: defaultDate.initialDate,
    finalDate: defaultDate.finalDate
  })

  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    parametrizationCurveStoretHeader
  )
  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('parametrizationCurveStoretHeader')) {
      encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)
    } else {
      localStorage.removeItem('parametrizationCurveStoretHeader')
      encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)
    }
  }, [headers])

  const exportData = useCallback(async () => {}, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )
  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('parametrizationCurveStoretHeader')) {
      const newHeader = decrypterHeaderStorange(
        'parametrizationCurveStoretHeader'
      )
      newHeader[0].columns[0] = {
        id: newHeader[0].columns[0].id,
        name: newHeader[0].columns[0].name,
        show: newHeader[0].columns[0].show
      }
      updateHeaders(newHeader)
    }
  }, [updateHeaders])
  const handleUpdateParams = useCallback(
    ({ name, value }: IUpdateParams) => {
      if (name === 'initialDate') {
        setCurrentyDate(prev => ({ ...prev, initialDate: value }))
      }
      if (name === 'finalDate') {
        setCurrentyDate(prev => ({ ...prev, finalDate: value }))
      }

      updateParams(draft => {
        draft[name] = value
      })
    },
    [updateParams]
  )

  const handlePageChange = useCallback(async (_page: number) => {
    setLoading(true)
  }, [])
  const getData = useCallback(
    async (search: any, page: any, total: any, isQuery: boolean) => {
      setLoading(!(page > 1))
      // await getMaintence()
      _setFetching(true)

      try {
        const paramsToSend = {
          lojaIds: paramsHelper.parse(params.lojasIds),
          produtoIds: paramsHelper.parse(params.produtosIds),
          empresaIds: paramsHelper.parse(params.empresaIds),
          dataInicio:
            currentyDate.initialDate == null
              ? defaultDate.initialDate
              : currentyDate.initialDate,
          dataFim:
            currentyDate.finalDate == null
              ? defaultDate.finalDate
              : currentyDate.finalDate,
          search: search === null ? '' : search
        }
        encrypteHeaderStorange('parametrizationCurveStoretHeader', headers)

        handleStorange()
        parametrizationCurves
          .getParamsCurveStore(search, page, 50, paramsToSend)
          .then(response => {
            const newData = [...data, ...response.data?.data]
            if (isQuery || page === 1) {
              updateData(response.data?.data)
            } else {
              updateData(newData)
            }
            setLoading(false)
            _setFetching(false)
            const pagination = response.data.pagination
            const xTotal = pagination.totalRows
            updatePagination(response.data.pagination)
            setTotal(xTotal)
            setPageCurrent(page)
          })
          .catch(() => {
            updatePagination({
              actualPage: 0,
              rows: 0,
              totalPages: 0,
              totalRows: 0,
              skip: 0
            })
            updateData([])
            setLoading(false)
            _setFetching(false)
          })
      } catch {
        setLoading(false)
      }
    },
    [
      params.lojasIds,
      params.produtosIds,
      params.empresaIds,
      currentyDate.initialDate,
      currentyDate.finalDate,
      defaultDate.initialDate,
      defaultDate.finalDate,
      headers,
      handleStorange,
      data,
      updateData,
      updatePagination
    ]
  )

  const updateDataCurve = useCallback(async () => {
    setLoading(true)

    try {
      const { data: _res } = await parametrizationCurves.postParamsCurveStore({
        lojaId: paramsEdit.lojaIds[0].value,
        produtoIds: paramsEdit.produtoIds.map(item => item.value),
        dataInicio: new Date(paramsEdit.dataInicio),
        dataFim: new Date(paramsEdit.dataFim)
      })
      toast({
        title: 'Sucesso.',
        description: 'Novas Lojas Loja editado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [paramsEdit, toast, getData, setSelecteds])

  const deleteData = useCallback(async () => {
    setLoading(true)

    try {
      await parametrizationCurves.removeParamsCurveStore({
        ids: selecteds
      })
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      getData(null, 1, 50, false)
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
      getData(null, 1, 50, false)
    }
    setSelectedOne(0)
  }, [selecteds, toast, getData, setSelecteds, updateEditParams])

  const createData = useCallback(async () => {
    setLoading(true)
    try {
      await parametrizationCurves.postParamsCurveStore({
        lojaId: paramsEdit.lojaIds[0].value,
        produtoIds: paramsEdit.produtoIds.map(item => item.value),
        dataInicio: new Date(paramsEdit.dataInicio),
        dataFim: new Date(paramsEdit.dataFim)
      })
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })
      toast({
        title: 'Sucesso.',
        description: 'Novas Lojas cadastrado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setSelecteds([])
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoading(false)
    }
    setSelectedOne(0)
    getData(null, 1, 50, false)
  }, [
    getData,
    paramsEdit.lojaIds,
    paramsEdit.produtoIds,
    paramsEdit.dataInicio,
    paramsEdit.dataFim,
    updateEditParams,
    toast,
    setSelecteds
  ])

  // Filtros da aba Compradores: só os que o endpoint aplica (a empresa é a do topo da tela)
  const filtersList = useMemo<ILayoutFilter[]>(() => {
    return [
      {
        id: 'fornecedoresIds',
        label: 'Fornecedores',
        filterId: 'providersForPurchase',
        type: 'select-pagination',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'categoriasCompactasIds',
        label: 'Categorias Compactas',
        filterId: 'compactCategories',
        type: 'select-pagination',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      },
      {
        id: 'compradores',
        label: 'Compradores',
        filterId: 'compradores',
        type: 'select-pagination',
        isMulti: true,
        isRequired: false,
        isDisabled: false
      }
    ]
  }, [])

  const handleEditParams = useCallback(
    async (index: any) => {
      const dataResponse = await data
      const idData = await dataResponse.filter(x => x.id === Number(index))
      if (idData.length > 0) {
        handleUpdateEditParams({
          name: 'lojaIds',
          value: [
            {
              label: idData[0].lojaId
                ? idData[0].lojaId + ' - ' + idData[0].lojaDescricao
                : '',
              value: idData[0].lojaId
            }
          ]
        })
        handleUpdateEditParams({
          name: 'produtoIds',
          value: [
            {
              label: idData[0].produtoId
                ? idData[0].produtoId + ' - ' + idData[0].produtoDescricao
                : '',
              value: idData[0].produtoId
            }
          ]
        })
        handleUpdateEditParams({
          name: 'dataInicio',
          value: new Date(idData[0].dataInicio).toISOString().split('T')[0]
        })
        handleUpdateEditParams({
          name: 'dataFim',
          value: new Date(idData[0].dataFim).toISOString().split('T')[0]
        })
      }
    },
    [handleUpdateEditParams, data]
  )

  const handleSelect = useCallback(
    async (index: any) => {
      const supply = index['id-id']
      let verifyUpdate = selecteds.length
      if (!selecteds.includes(supply)) {
        verifyUpdate++
        setSelecteds([...selecteds, supply])
      } else {
        verifyUpdate--
        setSelecteds(selecteds.filter((prop: any) => prop !== supply))
      }
      if (verifyUpdate === 1) {
        handleEditParams(index['id-id'])
      } else {
        updateEditParams({
          lojaIds: [],
          produtoIds: [],
          dataInicio: new Date().toDateString(),
          dataFim: new Date().toDateString()
        })
      }
    },
    [selecteds, setSelecteds, handleEditParams, updateEditParams]
  )

  const clearAllFilters = useCallback(() => {
    updateParams({
      lojasIds: [],
      mundoIds: [],
      categoriasCompactasIds: [],
      fornecedoresIds: [],
      produtosIds: [],
      empresaIds: [],
      statusIds: [],
      curvaAbc: [],
      curvaIgualZero: false,
      compradores: [],
      periodo: new Date().toISOString(),
      produtoSemFornecedor: false,
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
  }, [updateParams, defaultDate.initialDate, defaultDate.finalDate])

  const clearForm = useCallback(() => {
    if (selecteds.length === 0) {
      updateEditParams({
        lojaIds: [],
        produtoIds: [],
        dataInicio: new Date().toDateString(),
        dataFim: new Date().toDateString()
      })
    }
  }, [selecteds.length, updateEditParams])
  // Alcadas
  const getDataAlcadas = useCallback(
    async (id: any, forceReload = false) => {
      // Verificar cache se não for reload forçado
      if (!forceReload && isDataLoaded(id, 'alcadas')) {
        return
      }

      const seq = nextRequest('alcadas')
      setLoadingAlcadas(true)
      setCompanieId(id)
      handleStorange()

      try {
        ParametroCompra.getListaAlcadasCompras(id)
          .then(({ data: { data } }) => {
            if (!isLatestRequest('alcadas', seq)) return
            updateDataAlcadas(draft => {
              return draft.filter(item => item.status === 99).concat(data)
            })
            setCompanieId(id)
            updatePagination(pagination)
            markDataAsLoaded(id, 'alcadas')
            setLoadingAlcadas(false)
          })
          .catch(() => {
            if (!isLatestRequest('alcadas', seq)) return
            updateDataAlcadas([])
            setLoadingAlcadas(false)
          })
      } catch {
        setLoadingAlcadas(false)
      }
    },
    [
      handleStorange,
      updateDataAlcadas,
      updatePagination,
      pagination,
      isDataLoaded,
      markDataAsLoaded,
      nextRequest,
      isLatestRequest
    ]
  )

  const createAlcadas = useCallback(
    async (obj: any) => {
      let success = false
      setLoading(true)
      try {
        await ParametroCompra.criarAlcadasCompras(obj)

        // Limpar cache para forçar recarregamento
        clearDataCache()

        toast({
          title: 'Sucesso.',
          description: 'Nova Alcada cadastrado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelecteds([])
        success = true
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      } finally {
        setLoading(false)
      }
      setSelectedOne(0)
      getDataAlcadas(companieId, true)
      // Modal só fecha no sucesso (no erro o usuário mantém o que digitou)
      return success
    },
    [getDataAlcadas, companieId, toast, setSelecteds, clearDataCache]
  )

  const handleSelectAlcada = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selectedAlcada
      if (newSelected === similarProduct) {
        setSelectedAlcada(0)
        setupdateAlcadaOb(null)
      } else {
        setSelectedAlcada(similarProduct)

        const dataOne = dataAlcadas.filter(
          (x: { id: any }) => x.id === similarProduct
        )[0]
        setupdateAlcadaOb(dataOne)
      }
    },
    [dataAlcadas, selectedAlcada, setupdateAlcadaOb]
  )
  const deleteDataAlcada = useCallback(async () => {
    setLoading(true)
    try {
      await ParametroCompra.removerAlcadasCompras(selectedAlcada)
      setupdateAlcadaOb(null)

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      getDataAlcadas(companieId, true)
      setSelectedAlcada(0)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      getDataAlcadas(companieId, true)
      setSelectedAlcada(0)
    } finally {
      setLoading(false)
    }
  }, [selectedAlcada, setupdateAlcadaOb, toast, getDataAlcadas, companieId])

  const editDataAlcada = useCallback(
    async (obj: any) => {
      let success = false
      setLoading(true)

      try {
        const { data: _res } = await ParametroCompra.editarAlcadasCompras(obj)
        toast({
          title: 'Sucesso.',
          description: 'Parametro Compra - Alcadas editado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelectedAlcada(0)
        success = true
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      } finally {
        setLoading(false)
      }
      if (success) {
        setSelectedAlcada(0)
        setupdateAlcadaOb(null)
      }

      getDataAlcadas(companieId, true)
      return success
    },
    [getDataAlcadas, companieId, toast, setupdateAlcadaOb]
  )

  //
  // Ferias
  const getDataFerias = useCallback(
    async (id: any, forceReload = false) => {
      // Verificar cache se não for reload forçado
      if (!forceReload && isDataLoaded(id, 'ferias')) {
        return
      }

      const seq = nextRequest('ferias')
      setLoadingFerias(true)
      setCompanieId(id)
      handleStorange()

      try {
        ParametroCompra.getFeriasComprador(id)
          .then(({ data: { data } }) => {
            if (!isLatestRequest('ferias', seq)) return
            updateDataFerias(draft => {
              return draft.filter(item => item.status === 99).concat(data)
            })
            setCompanieId(id)
            updatePagination(pagination)
            markDataAsLoaded(id, 'ferias')
            setLoadingFerias(false)
          })
          .catch(() => {
            if (!isLatestRequest('ferias', seq)) return
            updateDataFerias([])
            setLoadingFerias(false)
          })
      } catch {
        setLoadingFerias(false)
      }
    },
    [
      handleStorange,
      updateDataFerias,
      updatePagination,
      pagination,
      isDataLoaded,
      markDataAsLoaded,
      nextRequest,
      isLatestRequest
    ]
  )

  const createFerias = useCallback(
    async (obj: any): Promise<boolean> => {
      let success = false
      setLoadingFerias(true)
      try {
        await ParametroCompra.createFeriasComprador(obj)

        // Limpar cache para forçar recarregamento
        clearDataCache()

        toast({
          title: 'Sucesso.',
          description: 'Nova Ferias cadastrada com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelecteds([])
        success = true
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingFerias(false)
      }
      setSelectedFerias(0)
      getDataFerias(companieId, true)
      // Modal só fecha no sucesso (no erro o usuário mantém o que digitou)
      return success
    },
    [getDataFerias, companieId, toast, setSelecteds, clearDataCache]
  )

  const handleSelectFerias = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selectedFerias

      if (newSelected === similarProduct) {
        setSelectedFerias(0)
        setupdateFeriasOb(null)
      } else {
        setSelectedFerias(similarProduct)

        const dataOne = dataFerias.filter(
          (x: { id: any }) => x.id === similarProduct
        )[0]
        setupdateFeriasOb(dataOne)
      }
    },
    [dataFerias, selectedFerias, setupdateFeriasOb]
  )
  const deleteDataFerias = useCallback(async () => {
    setLoadingFerias(true)
    try {
      await ParametroCompra.deleteFeriasComprador(selectedFerias)
      setupdateFeriasOb(null)

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      getDataFerias(companieId, true)
      setSelectedFerias(0)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoadingFerias(false)
      getDataFerias(companieId, true)
      setSelectedFerias(0)
    }
  }, [selectedFerias, setupdateFeriasOb, toast, getDataFerias, companieId])

  const editDataFerias = useCallback(
    async (obj: any): Promise<boolean> => {
      let success = false
      setLoadingFerias(true)

      try {
        await ParametroCompra.updateFeriasComprador(obj)
        toast({
          title: 'Sucesso.',
          description: 'Parametro Compra - Ferias editado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelectedFerias(0)
        // Evita que "Editar" reabra com os dados antigos
        setupdateFeriasOb(null)
        success = true
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingFerias(false)
      }

      getDataFerias(companieId, true)
      // Modal só fecha no sucesso (no erro mantém a seleção e o formulário)
      return success
    },
    [getDataFerias, companieId, toast, setupdateFeriasOb]
  )

  const postCargaFerias = useCallback(
    async (
      ferias: Array<{
        CompradorPrincipalEmail: string
        CompradorFeriasEmail: string
        FornecedorId: number
        DataInicio: string
        DataFim: string
      }>
    ): Promise<{ success: boolean; errors: string[] }> => {
      // O backend pode responder em camelCase ou PascalCase
      const pick = (obj: any) => ({
        success: obj?.success ?? obj?.Success,
        message: obj?.message ?? obj?.Message,
        data: obj?.data ?? obj?.Data
      })
      try {
        const { data: raw } = await ParametroCompra.cargaFerias(ferias)
        const res = pick(raw)
        if (res.success === false) {
          return {
            success: false,
            errors:
              Array.isArray(res.data) && res.data.length > 0
                ? res.data.map(String)
                : [res.message ?? 'Erro desconhecido']
          }
        }
        toast({
          title: 'Carga realizada com sucesso',
          description:
            res.message ?? `${ferias.length} registro(s) processado(s).`,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        await getDataFerias(companieId, true)
        return { success: true, errors: [] }
      } catch (error: any) {
        const errData = pick(error?.response?.data)
        const message = errData.message ?? 'Erro ao processar carga de férias.'
        const details: string[] = Array.isArray(errData.data)
          ? errData.data.map(String)
          : []
        return {
          success: false,
          errors: details.length > 0 ? details : [message]
        }
      }
    },
    [companieId, toast, getDataFerias]
  )

  // User
  const getDataUsers = useCallback(
    async (id: any, forceReload = false) => {
      // Verificar cache se não for reload forçado
      if (!forceReload && isDataLoaded(id, 'users')) {
        return
      }

      const seq = nextRequest('users')
      setLoadingUsers(true)
      setCompanieId(id)
      handleStorange()

      try {
        // A empresa é sempre a selecionada no topo da tela (não há filtro de empresa nesta aba)
        const filtro = {
          empresaId: id,
          compradores: params.compradores?.map((item: any) => item.value) || [],
          fornecedoresIds:
            params.fornecedoresIds?.map((item: any) => item.value) || [],
          categoriasCompactasIds:
            params.categoriasCompactasIds?.map((item: any) => item.value) || []
        }

        const response = await api.post(
          `/v1/ParametroCompras/Usuario/${filtro.empresaId}`,
          filtro
        )

        if (!isLatestRequest('users', seq)) return

        if (response.data.success) {
          updateDataUsers(response.data.data)
          setCompanieId(id)
          updatePagination(pagination)
          markDataAsLoaded(id, 'users')
          setLoadingUsers(false)
          setSelectedUser(0)
        } else {
          // success: false com 200 — sem isso o loader ficava ligado para sempre
          updateDataUsers([])
          setLoadingUsers(false)
          setSelectedUser(0)
        }
      } catch (error: any) {
        if (!isLatestRequest('users', seq)) return
        updateDataUsers([])
        setLoadingUsers(false)
        setSelectedUser(0)
      }
    },
    [
      handleStorange,
      updateDataUsers,
      updatePagination,
      pagination,
      params.compradores,
      params.fornecedoresIds,
      params.categoriasCompactasIds,
      isDataLoaded,
      markDataAsLoaded,
      nextRequest,
      isLatestRequest
    ]
  )

  // Devolve o sucesso real para o modal decidir se fecha (antes: sempre "sucesso")
  const createUsers = useCallback(
    async (obj: any): Promise<boolean> => {
      let success = false
      setLoadingUsers(true)
      try {
        await ParametroCompra.criarUsuarioCompras(obj)

        // Limpar cache para forçar recarregamento
        clearDataCache()

        toast({
          title: 'Sucesso.',
          description: 'Nova configuracao de Usuario cadastrado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        success = true
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setLoadingUsers(false)
      }
      setSelectedUser(0)
      getDataUsers(companieId, true)
      return success
    },
    [getDataUsers, companieId, toast, clearDataCache]
  )

  const handleSelectUser = useCallback(
    async (index: any) => {
      const similarProduct = index['id-id']
      const newSelected = selectedUser

      if (newSelected === similarProduct) {
        setSelectedUser(0)
        setupdateAlcadaOb(null)
      } else {
        setSelectedUser(similarProduct)

        const allUserData = dataUser.filter(
          (x: { idUsuarioCompras: any }) =>
            x.idUsuarioCompras === similarProduct
        )
        setupdateUserOb(allUserData)
      }
    },
    [dataUser, selectedUser, setupdateAlcadaOb, setupdateUserOb]
  )

  const deleteDataUser = useCallback(async () => {
    setLoadingUsers(true)
    try {
      await ParametroCompra.removerUsuarioCompras(selectedUser.toString(), 'v1')
      setupdateUserOb([])

      toast({
        title: 'Sucesso.',
        description: 'Registro deletado com Sucesso',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      setLoadingUsers(false)
      getDataUsers(companieId, true)
      setSelectedUser(0)
    } catch (error: any) {
      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setLoadingUsers(false)
      getDataUsers(companieId, true)
      setSelectedUser(0)
    }
  }, [selectedUser, setupdateUserOb, toast, getDataUsers, companieId])

  const editDataUser = useCallback(
    async (obj: any, skipRefresh = false) => {
      // Prevenir múltiplas chamadas simultâneas
      if (loadingUser) {
        return Promise.reject(new Error('Operação já em andamento'))
      }

      setLoadingUsers(true)

      try {
        await ParametroCompra.editarUsuarioCompras(obj)

        toast({
          title: 'Sucesso.',
          description: 'Parametro Compra - User editado com Sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
        setSelectedUser(0)

        // Só atualiza lista se não for skipRefresh (usado em operações em lote)
        if (!skipRefresh) {
          await getDataUsers(companieId, true)
        } else {
          setLoadingUsers(false)
        }
      } catch (error: any) {
        const message =
          error.response?.status === 400
            ? error.response?.data?.message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
        setSelectedUser(0)
        setLoadingUsers(false)
        throw error
      }
    },
    [getDataUsers, companieId, toast, loadingUser]
  )

  // Cronograma
  const getCronogramaCompra = useCallback(
    async (empresaId: number, usuarioId?: number, forceReload = false) => {
      // Usar selectedComprador ou usuarioId passado como parâmetro
      let userId: number | null = usuarioId || selectedComprador

      if (!userId) {
        // Fallback: pegar userid do token se não tiver comprador selecionado
        userId = getPurchaseConfigTokenClaims()?.userid ?? null
      }

      // Cache por empresa + comprador (os dados do calendário dependem dos dois)
      const cacheKey = `${empresaId}:${userId}`
      if (!forceReload && isDataLoaded(cacheKey, 'cronograma')) {
        return
      }

      const seq = nextRequest('cronograma')
      // Enquanto carrega, a tela não tem dono definido: bloqueia o Salvar
      cronogramaOwner.current = null
      setCronogramaLoadFailed(false)
      setLoadingCronograma(true)
      try {
        if (!userId) {
          throw new Error('UserId não encontrado')
        }

        // Usar os novos endpoints com userid
        const response = await api.get(
          `/v1/ParametroCompras/Cronograma/${empresaId}/Usuario/${userId}`
        )
        if (!isLatestRequest('cronograma', seq)) return
        const loaded: ScheduleItem[] = response.data?.data || []
        setCronogramaCompra(loaded)
        setCronogramaBaseline(serializeCronograma(loaded))
        cronogramaOwner.current = {
          empresaId: Number(empresaId),
          usuarioId: Number(userId)
        }

        // Marcar como carregado no cache
        markDataAsLoaded(cacheKey, 'cronograma')
      } catch (error: any) {
        if (!isLatestRequest('cronograma', seq)) return
        setCronogramaCompra([])
        setCronogramaBaseline('')

        const res = error?.response
        if (res?.status === 400) {
          // Comprador sem cronograma cadastrado: lista vazia legítima (pode cadastrar e salvar)
          if (userId) {
            cronogramaOwner.current = {
              empresaId: Number(empresaId),
              usuarioId: Number(userId)
            }
          }
          return
        }
        // Falha real: calendário vazio não representa o banco, então o Salvar fica bloqueado
        setCronogramaLoadFailed(true)
        toast({
          title: 'Erro ao buscar cronograma.',
          description: res?.data?.message
            ? res?.data?.message
            : 'Erro ao processar solicitação.',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      } finally {
        if (isLatestRequest('cronograma', seq)) setLoadingCronograma(false)
      }
    },
    [
      toast,
      selectedComprador,
      isDataLoaded,
      markDataAsLoaded,
      nextRequest,
      isLatestRequest
    ]
  )

  // Alterações no calendário ainda não gravadas (comparando com o que veio do banco)
  const hasUnsavedCronograma = useMemo(
    () =>
      !loadingCronograma &&
      serializeCronograma(cronogramaCompra) !== cronogramaBaseline,
    [loadingCronograma, cronogramaCompra, cronogramaBaseline]
  )

  // true = pode seguir (não há alterações ou o usuário aceitou descartá-las)
  const confirmDiscardCronograma = useCallback(() => {
    if (!hasUnsavedCronograma) return true
    return window.confirm(
      'Existem alterações não salvas no cronograma. Deseja descartá-las?'
    )
  }, [hasUnsavedCronograma])

  const handleCronogromaCompra = useCallback(async (novoItem: ScheduleItem) => {
    setCronogramaCompra(prev => [...prev, novoItem])
  }, [])

  const deleteCronogromaCompra = useCallback(
    async (itemParaRemover: ScheduleItem) => {
      setCronogramaCompra(prev =>
        prev.filter(item => item.id !== itemParaRemover.id)
      )
    },
    []
  )

  const createCronogramaCompra = useCallback(async () => {
    // O salvar substitui o cronograma inteiro do comprador: só grava se o calendário
    // na tela foi carregado com sucesso e pertence à empresa/comprador selecionados
    const owner = cronogramaOwner.current
    const ownerMismatch =
      !owner ||
      owner.empresaId !== Number(companieId) ||
      (!!selectedComprador && owner.usuarioId !== selectedComprador)

    if (loadingCronograma || cronogramaLoadFailed || ownerMismatch) {
      toast({
        title: 'Cronograma não salvo.',
        description:
          'O cronograma na tela não foi carregado corretamente para a empresa/comprador selecionados. Recarregue antes de salvar para não sobrescrever os dados.',
        status: 'warning',
        duration: 5000,
        isClosable: true
      })
      return
    }

    setLoadingCronograma(true)

    const { empresaId, usuarioId } = owner
    const salvo = cronogramaCompra

    const payload: AtualizarCronogramaComprasCommand = {
      empresaId,
      usuarioId,
      cronogramaCompras: salvo.map(item => ({
        lojaId: item.lojaId,
        fornecedorId: item.fornecedorId,
        categoriaCompactaId: item.categoriaCompactaId,
        diaDaSemana: item.diaDaSemana
      }))
    }

    try {
      await ParametroCompra.atualizarCronogramaCompras(payload)
      setCronogramaBaseline(serializeCronograma(salvo))

      toast({
        title: 'Sucesso.',
        description: 'Cronograma atualizado com sucesso.',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar.',
        description:
          error?.response?.status === 400 && error?.response?.data?.message
            ? error.response.data.message
            : 'Não foi possível salvar o cronograma.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoadingCronograma(false)
    }
  }, [
    companieId,
    selectedComprador,
    loadingCronograma,
    cronogramaLoadFailed,
    cronogramaCompra,
    toast
  ])

  // Retorna true no sucesso (o botão usa para o estado de carregamento)
  const uploadCronogramaCompra = useCallback(
    async (file: File): Promise<boolean> => {
      const formData = new FormData()
      formData.append('file', file)

      try {
        await ParametroCompra.uploadCronogramaCompras(formData)
        toast({
          title: 'Sucesso!',
          description: 'O arquivo de cronograma foi enviado com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        // Recarrega a empresa/comprador da tela (antes: empresa 1000 fixa e sem ignorar o cache)
        if (companieId) {
          getCronogramaCompra(
            Number(companieId),
            selectedComprador || undefined,
            true
          )
        }
        return true
      } catch (error: any) {
        // Tratamento específico para erro 400 com mensagem da API
        const errorMessage =
          error.response?.status === 400 && error.response?.data?.message
            ? error.response.data.message
            : 'Não foi possível enviar o arquivo. Verifique o formato e tente novamente.'

        toast({
          title: 'Erro no Upload',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return false
      }
    },
    [companieId, selectedComprador, getCronogramaCompra, toast]
  )

  // Função para carregar fornecedores do comprador
  const getFornecedoresByComprador = useCallback(
    async (empresaId: number, usuarioId: number) => {
      try {
        const response = await api.get(
          `/v1/ParametroCompras/Usuario/${usuarioId}/Fornecedores/${empresaId}`
        )

        if (response.data.success && response.data.data) {
          return response.data.data
        }
      } catch (error: any) {
        if (error.response?.status !== 400) {
          toast({
            title: 'Erro ao carregar fornecedores.',
            description:
              'Não foi possível carregar os fornecedores para este comprador.',
            status: 'warning',
            duration: 3000,
            isClosable: true
          })
        }
      }
      return []
    },
    [toast]
  )

  // Troca o comprador do calendário (sem perguntar; quem chama decide)
  const selectComprador = useCallback(
    (compradorId: number) => {
      setSelectedComprador(compradorId)
      selectedCompradorRef.current = compradorId

      // Recarregar dados para o comprador selecionado
      if (companieId) {
        const empresaIdNum = Number(companieId)
        // Limpa o calendário e invalida o cache antes de buscar dados do novo comprador
        setCronogramaCompra([])
        setCronogramaBaseline('')
        clearDataCache()
        getCronogramaCompra(empresaIdNum, compradorId, true)
      }
    },
    [companieId, getCronogramaCompra, clearDataCache]
  )

  const handleCompradorChange = useCallback(
    (compradorId: number) => {
      if (compradorId === selectedCompradorRef.current) return
      // Trocar de comprador descarta o que não foi salvo no calendário
      if (!confirmDiscardCronograma()) return
      selectComprador(compradorId)
    },
    [confirmDiscardCronograma, selectComprador]
  )

  // Compradores
  const getCompradores = useCallback(async () => {
    setLoadingCompradores(true)
    try {
      const response = await api.get('/v1/ControleFornecedor/Compradores')

      if (response.data.success && response.data.data) {
        const compradoresProcessados = response.data.data.map((item: any) => ({
          id: item.id || item.usuarioId || item.compradorId,
          nome:
            item.compradorEmail ||
            item.nomeUsuario ||
            item.compradorNome ||
            item.email ||
            'Sem nome',
          email: item.email || ''
        }))

        setCompradores(compradoresProcessados)

        // Admin sem comprador definido: seleciona (e carrega) o primeiro da lista.
        // Lê perfil e seleção atuais (token/ref), não valores capturados na criação do callback.
        if (
          isPurchaseConfigAdmin() &&
          !selectedCompradorRef.current &&
          compradoresProcessados.length > 0
        ) {
          selectComprador(compradoresProcessados[0].id)
        }
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar compradores',
        description: 'Não foi possível carregar a lista de compradores',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoadingCompradores(false)
    }
  }, [selectComprador, toast])

  const initializeComprador = useCallback(() => {
    const claims = getPurchaseConfigTokenClaims()
    const userId = claims?.userid

    setIsAdmin(isPurchaseConfigAdmin(claims))

    // Se não há comprador selecionado, usar o usuário logado como padrão
    if (!selectedCompradorRef.current && userId) {
      const id = parseInt(userId)
      selectedCompradorRef.current = id
      setSelectedComprador(id)
    }
  }, [])

  // E-mail do comprador cujo calendário está na tela (export / novos cards)
  const selectedCompradorEmail = useMemo(() => {
    const claims = getPurchaseConfigTokenClaims()
    const tokenUserId = Number(claims?.userid)
    const compradorId = selectedComprador ?? tokenUserId
    const comprador = compradores.find(c => c.id === compradorId)
    const fromList =
      comprador?.email ||
      (comprador?.nome && comprador.nome.includes('@') ? comprador.nome : '')
    if (fromList) return fromList
    // Calendário do próprio usuário logado: o e-mail do token é o do comprador
    if (compradorId && tokenUserId === compradorId) {
      return claims?.email ?? ''
    }
    return ''
  }, [compradores, selectedComprador])

  // Demais

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: false
      }
    ]
  }, [handleSelect, selecteds])

  const tableInteractionAlcada = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelectAlcada,
        selecteds: selectedAlcada,
        isMulti: false
      }
    ]
  }, [handleSelectAlcada, selectedAlcada])
  const tableInteractionUser = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelectUser,
        selecteds: selectedUser,
        isMulti: false
      }
    ]
  }, [handleSelectUser, selectedUser])

  const tableInteractionFerias = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <FaHistory />,
        onChange: handleSelectFerias,
        selecteds: selectedFerias,
        isMulti: false
      }
    ]
  }, [handleSelectFerias, selectedFerias])

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'cronogramaCompra',
            data: cronogramaCompra,
            parser: parseParametrizationPurchaseCronogramaExport
          }
        ]
      },
      {
        type: 'cronogramaUpload'
      },
      {
        type: 'purchaseCronogramaHistoric',
        data: cronogramaCompra,
        onUpdate: setCronogramaCompra
      }
    ]
  }, [cronogramaCompra])

  const actionsAlcada = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'csv',
            entity: 'purchaseAlcadasData'
          }
        ]
      },
      {
        type: 'purchaseAlcadasHistoric',
        data: dataAlcadas,
        onUpdate: updateDataAlcadas
      }
    ]
  }, [dataAlcadas, updateDataAlcadas])

  const actionsUser = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'csv',
            entity: 'purchaseUserData'
          },
          {
            type: 'csv',
            entity: 'purchaseUserModeloTabelas'
          }
        ]
      },
      {
        type: 'userUpload'
      },
      {
        type: 'purchaseUserHistoric',
        data: data,
        onUpdate: updateData
      }
    ]
  }, [data, updateData])

  const actionsFerias = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'csv',
            entity: 'purchaseFeriasData'
          }
        ]
      },
      {
        type: 'purchaseFeriasCarga'
      },
      {
        type: 'purchaseFeriasHistoric',
        data: dataFerias,
        onUpdate: updateDataFerias
      }
    ]
  }, [dataFerias, updateDataFerias])

  // Só define a empresa: a tela (PurchaseConfig) busca os dados da aba ativa
  const handleCompanieId = useCallback(async (companie: any) => {
    setCompanieId(companie?.value ?? '')
  }, [])

  // Dados já formatados para a grade (só recalcula quando a lista muda)
  const dataParsed = useMemo(
    () => parseParametrizatioCurveStoreData(data),
    [data]
  )
  const dataAlcadasParsed = useMemo(
    () => parseConfigAlcadas(dataAlcadas),
    [dataAlcadas]
  )
  const dataUserParsed = useMemo(() => parseConfigUser(dataUser), [dataUser])
  const dataFeriasParsed = useMemo(
    () => parseConfigFerias(dataFerias),
    [dataFerias]
  )

  const value = useMemo<IConfigPurchaseParamProvider>(
    () => ({
      loading,
      loadingEdit,
      data: dataParsed,
      params,
      headers,
      fetching,
      actions,
      tableInteraction,
      selected,
      filterEdit,
      paramsEdit,
      paramsDates,
      tooglesTable,
      filename,
      query,
      filters,
      hiddenColumns,
      handlePageChange,
      pagination,
      fastQuery,
      filtersList,
      toggleColumn,
      handleUpdateParams,
      handleSelect,
      getCenters,
      handleParamsDate,
      setQuery,
      getData,
      exportData,
      handleCompanieId,
      createData,
      handleUpdateEditParams,
      handleToogleTable,
      updateDataCurve,
      deleteData,
      setFilters,
      setFilename,
      defaultDate,
      currentyDate,
      selecteds,
      pageCurrent,
      total,
      updateUserOb,
      clearForm,
      handleSelectAll,
      dataOriginal: data,
      clearAllFilters,
      setActiveTimeLine,
      activeTimeline,
      getDataAlcadas,
      companieId,
      createAlcadas,
      dataAlcadas: dataAlcadasParsed,
      loadingAlcadas,
      updateAlcadaOb,
      selectedAlcada,
      handleSelectAlcada,
      tableInteractionAlcada,
      deleteDataAlcada,
      editDataAlcada,
      actionsAlcada,
      getDataUsers,
      dataUser: dataUserParsed,
      loadingUser,
      createUsers,
      actionsUser,
      tableInteractionUser,
      deleteDataUser,
      selectedUser,

      dataFerias: dataFeriasParsed,
      loadingFerias,
      createFerias,
      actionsFerias,
      tableInteractionFerias,
      getDataFerias,
      deleteDataFerias,
      editDataFerias,
      postCargaFerias,
      editDataUser,
      updateFeriasOb,
      selectedFerias,
      cronogramaCompra,
      loadingCronograma,
      cronogramaLoadFailed,
      getCronogramaCompra,
      handleCronogromaCompra,
      deleteCronogromaCompra,
      createCronogramaCompra,
      setCronogramaCompra,
      uploadCronogramaCompra,
      hasUnsavedCronograma,
      confirmDiscardCronograma,
      // Compradores
      compradores,
      selectedComprador,
      selectedCompradorEmail,
      loadingCompradores,
      isAdmin,
      getCompradores,
      handleCompradorChange,
      initializeComprador,
      getFornecedoresByComprador
    }),
    [
      loading,
      loadingEdit,
      dataParsed,
      params,
      headers,
      fetching,
      actions,
      tableInteraction,
      selected,
      filterEdit,
      paramsEdit,
      paramsDates,
      tooglesTable,
      filename,
      query,
      filters,
      hiddenColumns,
      handlePageChange,
      pagination,
      fastQuery,
      filtersList,
      toggleColumn,
      handleUpdateParams,
      handleSelect,
      getCenters,
      handleParamsDate,
      getData,
      exportData,
      handleCompanieId,
      createData,
      handleUpdateEditParams,
      handleToogleTable,
      updateDataCurve,
      deleteData,
      setFilters,
      defaultDate,
      currentyDate,
      selecteds,
      pageCurrent,
      total,
      updateUserOb,
      clearForm,
      handleSelectAll,
      data,
      clearAllFilters,
      activeTimeline,
      getDataAlcadas,
      companieId,
      createAlcadas,
      dataAlcadasParsed,
      loadingAlcadas,
      updateAlcadaOb,
      selectedAlcada,
      handleSelectAlcada,
      tableInteractionAlcada,
      deleteDataAlcada,
      editDataAlcada,
      actionsAlcada,
      getDataUsers,
      dataUserParsed,
      loadingUser,
      createUsers,
      actionsUser,
      tableInteractionUser,
      deleteDataUser,
      selectedUser,
      dataFeriasParsed,
      loadingFerias,
      createFerias,
      actionsFerias,
      tableInteractionFerias,
      getDataFerias,
      deleteDataFerias,
      editDataFerias,
      postCargaFerias,
      editDataUser,
      updateFeriasOb,
      selectedFerias,
      cronogramaCompra,
      loadingCronograma,
      cronogramaLoadFailed,
      getCronogramaCompra,
      handleCronogromaCompra,
      deleteCronogromaCompra,
      createCronogramaCompra,
      uploadCronogramaCompra,
      hasUnsavedCronograma,
      confirmDiscardCronograma,
      compradores,
      selectedComprador,
      selectedCompradorEmail,
      loadingCompradores,
      isAdmin,
      getCompradores,
      handleCompradorChange,
      initializeComprador,
      getFornecedoresByComprador
    ]
  )

  return (
    <ParametrizationConfigPurchaseContext.Provider value={value}>
      {children}
    </ParametrizationConfigPurchaseContext.Provider>
  )
}

export default ParametrizationConfigPurchaseProvider
export const ParametrizationConfigPurchaseContext =
  createContext<IConfigPurchaseParamProvider>(
    {} as IConfigPurchaseParamProvider
  )
